import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { financialPlanningPages } from '@app/const/route.const';
import {
  MeetingGuidanceWidget,
  useGetMeetingGuidanceQuery,
  useGetMeetingGuidanceTemplatesQuery,
  useSaveMeetingGuidanceIntakeMutation,
} from '@app/features/meetingGuidance';
import type {
  MeetingIntakePriority,
  MeetingIntakeSaveEntry,
} from '@app/features/meetingGuidance';

const buildEntries = (
  value: string,
  priority: MeetingIntakePriority,
): MeetingIntakeSaveEntry[] =>
  value
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((text) => ({ text, priority }));

const entriesToLines = (entries: { text: string }[]) =>
  entries.map((entry) => entry.text).join('\n');

export const MeetingGuidancePage = () => {
  const { planResultRootId, planResultTypeId } = useParams<{
    planResultRootId: string;
    planResultTypeId: string;
  }>();
  const navigate = useNavigate();
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [clientTopics, setClientTopics] = useState('');
  const [advisorPriorities, setAdvisorPriorities] = useState('');
  const [clientPriority, setClientPriority] = useState<MeetingIntakePriority>('medium');
  const [advisorPriority, setAdvisorPriority] = useState<MeetingIntakePriority>('medium');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isPrepOpen, setIsPrepOpen] = useState(true);
  const [hasInitializedPrep, setHasInitializedPrep] = useState(false);
  const rootId = planResultRootId ?? '';
  const typeId = planResultTypeId ?? '';
  const { data: guidanceData } = useGetMeetingGuidanceQuery(
    {
      planResultRootId: rootId,
      planResultTypeId: typeId,
    },
    { skip: !planResultRootId || !planResultTypeId },
  );
  const { data: templatesData, isLoading: templatesLoading } =
    useGetMeetingGuidanceTemplatesQuery(
      {
        planResultRootId: rootId,
        planResultTypeId: typeId,
      },
      { skip: !planResultRootId || !planResultTypeId },
    );
  const [saveIntake, { isLoading: isSaving, isError: isSaveError, error: saveError }] =
    useSaveMeetingGuidanceIntakeMutation();

  const savedIntake = guidanceData?.meeting_intake;
  const hasSavedIntake =
    Boolean(savedIntake?.template_id) ||
    Boolean(savedIntake?.client_topics.length) ||
    Boolean(savedIntake?.advisor_priorities.length);
  const savedTemplateName = savedIntake?.selected_template?.name ?? 'default agenda';
  const savedClientTopicCount = savedIntake?.client_topics.length ?? 0;
  const savedAdvisorPriorityCount = savedIntake?.advisor_priorities.length ?? 0;
  const savedAdvisorPriorityLabel =
    savedAdvisorPriorityCount === 1 ? 'priority' : 'priorities';
  const prepSummary = hasSavedIntake
    ? [
        `Using ${savedTemplateName} with ${savedClientTopicCount} client topic(s)`,
        `and ${savedAdvisorPriorityCount} advisor ${savedAdvisorPriorityLabel}.`,
      ].join(' ')
    : 'No explicit meeting intent has been saved yet.';

  useEffect(() => {
    if (!savedIntake || hasInitializedPrep) {
      return;
    }

    setSelectedTemplateId(savedIntake.template_id ?? '');
    setClientTopics(entriesToLines(savedIntake.client_topics));
    setAdvisorPriorities(entriesToLines(savedIntake.advisor_priorities));
    setClientPriority(savedIntake.client_topics[0]?.priority ?? 'medium');
    setAdvisorPriority(savedIntake.advisor_priorities[0]?.priority ?? 'medium');
    setIsPrepOpen(!hasSavedIntake);
    setHasInitializedPrep(true);
  }, [hasInitializedPrep, hasSavedIntake, savedIntake]);

  if (!planResultRootId || !planResultTypeId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="par02Regular">
          Missing required parameters: planResultRootId and planResultTypeId
        </Typography>
      </Box>
    );
  }

  const handleSave = async () => {
    setSaveSuccess(false);
    try {
      await saveIntake({
        planResultRootId,
        planResultTypeId,
        intake: {
          template_id: selectedTemplateId || null,
          client_topics: buildEntries(clientTopics, clientPriority),
          advisor_priorities: buildEntries(advisorPriorities, advisorPriority),
        },
      }).unwrap();
      setSaveSuccess(true);
      setIsPrepOpen(false);
    } catch {
      setSaveSuccess(false);
    }
  };

  const saveErrorMessage = String(
    (saveError as { data?: { error?: string } } | undefined)?.data?.error ??
      'Failed to save meeting intake.',
  );

  const handleClose = () => {
    window.close();
    window.setTimeout(() => {
      navigate(financialPlanningPages.clientListUrl);
    }, 100);
  };

  const handleViewSignalCatalog = () => {
    navigate(
      financialPlanningPages.getMeetingGuidanceSignalCatalogUrl(
        planResultRootId,
        planResultTypeId,
      ),
    );
  };

  return (
    <Box
      sx={{
        width: '100vw',
        minHeight: '100vh',
        backgroundColor: 'grey.100',
      }}
    >
      <Box sx={{ p: 4, pb: 0 }}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="h5SMedium">Prepare Meeting</Typography>
                  <Typography variant="par02Regular" color="text.secondary">
                    Edit meeting intent and template inputs before using the prep brief
                    and live companion.
                  </Typography>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="outlined" onClick={handleViewSignalCatalog}>
                    View Signal Catalog
                  </Button>
                  <Button variant="outlined" onClick={() => setIsPrepOpen(!isPrepOpen)}>
                    {isPrepOpen ? 'Hide Prep Inputs' : 'Edit Prep Inputs'}
                  </Button>
                </Stack>
              </Box>
              {!isPrepOpen && (
                <Typography variant="par02Regular" color="text.secondary">
                  {prepSummary}
                </Typography>
              )}
              <Typography variant="par02Regular" color="text.secondary">
                The guidance below remains the meeting-ready output. These inputs are
                for the advisor preparation step.
              </Typography>
              {saveSuccess && <Alert severity="success">Meeting intake saved.</Alert>}
              {isSaveError && <Alert severity="error">{saveErrorMessage}</Alert>}
              <Collapse in={isPrepOpen}>
                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel id="meeting-template-label">Template</InputLabel>
                    <Select
                      labelId="meeting-template-label"
                      label="Template"
                      value={selectedTemplateId}
                      disabled={templatesLoading}
                      onChange={(event) => setSelectedTemplateId(event.target.value)}
                    >
                      <MenuItem value="">Default agenda</MenuItem>
                      {templatesData?.templates.map((template) => (
                        <MenuItem key={template.template_id} value={template.template_id}>
                          {template.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Client-entered topics"
                      helperText="One topic per line"
                      value={clientTopics}
                      onChange={(event) => setClientTopics(event.target.value)}
                    />
                    <FormControl sx={{ minWidth: 180 }}>
                      <InputLabel id="client-priority-label">Client Priority</InputLabel>
                      <Select
                        labelId="client-priority-label"
                        label="Client Priority"
                        value={clientPriority}
                        onChange={(event) =>
                          setClientPriority(event.target.value as MeetingIntakePriority)
                        }
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Advisor-entered priorities"
                      helperText="One priority per line"
                      value={advisorPriorities}
                      onChange={(event) => setAdvisorPriorities(event.target.value)}
                    />
                    <FormControl sx={{ minWidth: 180 }}>
                      <InputLabel id="advisor-priority-label">Advisor Priority</InputLabel>
                      <Select
                        labelId="advisor-priority-label"
                        label="Advisor Priority"
                        value={advisorPriority}
                        onChange={(event) =>
                          setAdvisorPriority(event.target.value as MeetingIntakePriority)
                        }
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <Box>
                    <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Prep Inputs'}
                    </Button>
                  </Box>
                </Stack>
              </Collapse>
            </Stack>
          </CardContent>
        </Card>
      </Box>
      <MeetingGuidanceWidget
        planResultRootId={planResultRootId}
        planResultTypeId={planResultTypeId}
        onClose={handleClose}
      />
    </Box>
  );
};
